import { Column } from '@ant-design/charts';
import { useCubeQuery } from '@cubejs-client/react';
import { VizState } from '@threatadvisor/react-components';
import { formatDate, formatDateTimeChart, useTranslation } from '@threatadvisor/react-utils';
import { Spin, Typography } from 'antd';
import React, { ReactElement, useId, useMemo } from 'react';
import { getPriorityScaleColors } from '../..';

export type AverageTimeClosureByPrioritProps = {
  clientUid: string;
  reportStart: string;
  reportEnd: string;
  securityEventApi: string;
};

export default function AverageTimeClosureByPriority(
  props: AverageTimeClosureByPrioritProps
): ReactElement {
  const { clientUid, reportStart, reportEnd } = props;

  const { t } = useTranslation('translation');

  const getPriorityQuery = (): VizState => {
    return {
      chartType: 'bar',
      pivotConfig: {
        x: ['ClosedDetections.priority'],
        y: ['measures'],
        fillMissingDates: true
      },
      query: {
        measures: ['ClosedDetections.avg_minutes_to_close'],
        dimensions: ['ClosedDetections.priority'],
        timeDimensions: [
          {
            dimension: 'ClosedDetections.closed_date',
            dateRange: [reportStart, reportEnd]
          }
        ],
        order: {
          'ClosedDetections.priority': 'asc'
        },
        filters: [
          {
            member: 'ClosedDetections.priority',
            operator: 'notContains',
            values: ['P5']
          },
          {
            member: 'ClosedDetections.client_uid',
            operator: 'equals',
            values: [clientUid]
          }
        ]
      }
    };
  };
  const getOverallQuery = (): VizState => {
    return {
      chartType: 'bar',
      pivotConfig: {
        x: [],
        y: ['measures'],
        fillMissingDates: true
      },
      query: {
        measures: ['ClosedDetections.avg_minutes_to_close'],
        timeDimensions: [
          {
            dimension: 'ClosedDetections.closed_date',
            dateRange: [reportStart, reportEnd]
          }
        ],
        filters: [
          {
            member: 'ClosedDetections.priority',
            operator: 'notContains',
            values: ['P5']
          },
          {
            member: 'ClosedDetections.client_uid',
            operator: 'equals',
            values: [clientUid]
          }
        ]
      }
    };
  };

  const { resultSet: priorityData, isLoading: isLoadingPriorityData } = useCubeQuery(
    getPriorityQuery().query!
  );
  const { resultSet: overallData, isLoading: isLoadingOverallData } = useCubeQuery(
    getOverallQuery().query!
  );

  function getAverageMinutes(resultSet) {
    return resultSet.chartPivot()[0]['ClosedDetections.avg_minutes_to_close'] < 1
      ? '< 1 min'
      : formatDateTimeChart(
          resultSet.chartPivot()[0]['ClosedDetections.avg_minutes_to_close'],
          false
        );
  }

  const overallDataTransform: { x: string; y: number; series: string }[] | null = useMemo(() => {
    if (priorityData) {
      const data: { x: string; y: number; series: string }[] = [];
      const results = priorityData.chartPivot(getPriorityQuery().pivotConfig);
      let seriesName = '';
      priorityData.seriesNames(getPriorityQuery().pivotConfig).forEach(({ key }) => {
        seriesName = key;
        data.push(
          ...results.map(result => ({
            x: t(result.x),
            y: result[key],
            series: key
          }))
        );
      });

      //including p1-p4
      if (!data.find(e => e.x === 'P1')) data.push({ x: 'P1', y: 0, series: seriesName });
      if (!data.find(e => e.x === 'P2')) data.push({ x: 'P2', y: 0, series: seriesName });
      if (!data.find(e => e.x === 'P3')) data.push({ x: 'P3', y: 0, series: seriesName });
      if (!data.find(e => e.x === 'P4')) data.push({ x: 'P4', y: 0, series: seriesName });
      return data.sort((a, b) => a.x.localeCompare(b.x));
    }
    return null;
  }, [priorityData]);

  const myId = useId();
  if (!window['chartRenders']) window['chartRenders'] = {};
  window['chartRenders'][myId] = 'Rendering';

  return (
    <>
      <h2 className="text-uppercase">Average Time to Closure by Priority Level</h2>

      <figure>
        <Typography.Title level={4}>
          Overall Average Time to Closure: {overallData ? getAverageMinutes(overallData) : <Spin />}
        </Typography.Title>
      </figure>
      <figure>
        {!isLoadingPriorityData && !isLoadingOverallData && overallDataTransform ? (
          <Column
            onReady={plot => {
              plot.chart.on('afterrender', args => {
                window['chartRenders'][myId] = 'Done';
              });
            }}
            {...{
              style: { maxWidth: 50 },
              colorField: 'x',
              legend: false,
              label: {
                // AT EACH Y POINT
                formatter: (val, itm) => formatDateTimeChart(itm.y, true),
                text: val => (val.y === 0 ? '' : val.y),
                textBaseline: 'bottom',
                style: {
                  becomeWrap: true,
                  wordWrapWidth: 40,
                  wordWrap: true
                }
              },
              ...getPriorityScaleColors(),
              theme: {
                type: 'threatadvisor'
              },

              tooltip: false,
              axis: {
                // ACROSS THE Y Axis... vertical in this case
                y: {
                  labelFormatter: (text: string, item: any, index: number) => {
                    return formatDateTimeChart(text, true);
                  }
                }
              }
            }}
            data={overallDataTransform}
            yField="y"
            xField="x"
          />
        ) : (
          <Spin />
        )}
        <figcaption>
          <Typography.Text type="secondary">
            Shows average time to closure by priority level between{' '}
            {formatDate(new Date(reportStart))} and {formatDate(new Date(reportEnd))}.
          </Typography.Text>
        </figcaption>
      </figure>
    </>
  );
}
